import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AdmissionErrorCode } from "@/api/session";
import { SessionProvider } from "@/auth/session-provider";
import type { NotificationStreamCheckpoint } from "@/features/notifications/notification-stream";
import { EmbeddedNotice } from "./components/embedded-notice";
import { EmbeddedHostProvider, useEmbeddedHost } from "./embedded-host-provider";
import { EmbeddedLayout } from "./embedded-layout";
import { EmbeddedNotifications } from "./embedded-notifications";
import { EmbeddedTokenBoundary } from "./embedded-token-boundary";
import type { ForbiddenReason } from "./protocol";

/**
 * `/embedded` 分支的根（设计 §8.3）。
 *
 * 与独立端的区别只在最外面两层：`EmbeddedHostProvider` + `EmbeddedTokenBoundary`
 * 取代了 `AuthenticationBoundary`；再往里的 `SessionProvider`、准入、RBAC、
 * 对象范围和 API Client 与独立端完全共用。
 *
 * 通知使用无界面的 `EmbeddedNotifications`，游标只保存在内存，收到后转发给宿主。
 * 独立端的通知中心、持久化游标与 Web Push 仍由 `NotificationProvider` 管理。
 */

const ADMISSION_FORBIDDEN_REASONS: Record<string, ForbiddenReason> = {
  [AdmissionErrorCode.NotProvisioned]: "not-provisioned",
  [AdmissionErrorCode.Inactive]: "inactive",
  [AdmissionErrorCode.NoRoles]: "no-roles",
};

/** 会话就绪后告诉宿主可以显示 iframe 了。 */
function EmbeddedReadySignal() {
  const { notifyReady } = useEmbeddedHost();
  useEffect(() => {
    notifyReady();
  }, [notifyReady]);
  return null;
}

/**
 * 处理宿主导航：首次建链还原深链，之后在同一个 iframe 文档内切换路由。
 * 所有路径都已由 `normalizeInitialPath` 限制在 `/embedded/**`。
 */
function EmbeddedNavigationController() {
  const { initialPath, navigationRequest } = useEmbeddedHost();
  const navigate = useNavigate();
  const handledNavigationSeq = useRef(0);

  useEffect(() => {
    if (initialPath && initialPath !== `${window.location.pathname}${window.location.search}`) {
      void navigate(initialPath, { replace: true });
    }
    // 只在建链拿到深链时还原一次，之后的导航归用户和路由。
  }, [initialPath, navigate]);

  useEffect(() => {
    if (!navigationRequest || navigationRequest.seq <= handledNavigationSeq.current) {
      return;
    }
    handledNavigationSeq.current = navigationRequest.seq;
    if (navigationRequest.path !== `${window.location.pathname}${window.location.search}`) {
      void navigate(navigationRequest.path, { replace: true });
    }
  }, [navigate, navigationRequest]);

  return null;
}

function EmbeddedSession() {
  const { notifyForbidden } = useEmbeddedHost();
  // Token 续期会让准入边界暂时卸载 children，游标随宿主会话保留以补拉这段空窗。
  const notificationCheckpoint = useRef<NotificationStreamCheckpoint>({});

  return (
    <SessionProvider
      persistCache={false}
      loadingVariant="inline"
      noticeComponent={EmbeddedNotice}
      onAdmissionDenied={(code) => {
        const reason = code ? ADMISSION_FORBIDDEN_REASONS[code] : undefined;
        if (reason) {
          notifyForbidden(reason);
        }
      }}
    >
      <EmbeddedReadySignal />
      <EmbeddedNavigationController />
      <EmbeddedNotifications checkpoint={notificationCheckpoint.current} />
      <EmbeddedLayout />
    </SessionProvider>
  );
}

export function EmbeddedRoot() {
  return (
    <EmbeddedHostProvider>
      <EmbeddedTokenBoundary>
        <EmbeddedSession />
      </EmbeddedTokenBoundary>
    </EmbeddedHostProvider>
  );
}
